import styled, { css } from 'styled-components';

/**
 * The ABF contract review palette, shared by the review page and every card it
 * composes (standard approved 2026-09-11). Cards carry these tokens themselves
 * rather than being re-skinned by a wrapper, so state colour still reads.
 */
export const INK = {
  strong: '#edf4ff',
  body: '#e5edf7',
  soft: '#c5d4e6',
  muted: '#a8b6ca',
  faint: '#91a4bb',
  accent: '#67c6f4',
  accentSoft: '#a6d8f5',
  line: '#29374b',
  lineStrong: '#3d526a',
  panel: '#0c1524',
  raised: '#0e1a2b',
  well: '#142033',
};

export const Card = styled.section`
  min-width: 0;
  padding: 1rem;
  border: 1px solid ${INK.line};
  border-radius: 14px;
  background: ${INK.panel};
`;

export const CardHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.8rem;

  h2 { margin: 0; color: ${INK.strong}; font-size: 0.95rem; font-weight: 800; text-wrap: balance; }
  p { margin: 0.3rem 0 0; color: ${INK.muted}; font-size: 0.74rem; line-height: 1.5; max-width: 62ch; }
`;

export const Eyebrow = styled.div`
  color: ${INK.faint};
  font-size: 0.6rem;
  font-weight: 850;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

export const Muted = styled.span`
  color: ${INK.muted};
`;

export const fieldBase = css`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 0.5rem 0.6rem;
  border: 1px solid ${INK.lineStrong};
  border-radius: 8px;
  background: ${INK.well};
  color: ${INK.body};
  color-scheme: dark;
  font: inherit;
  font-size: 0.76rem;

  &:focus { outline: none; border-color: ${INK.accent}; box-shadow: 0 0 0 2px rgba(56, 189, 248, 0.18); }
  &::placeholder { color: ${INK.faint}; }
`;

export const Input = styled.input`
  ${fieldBase}
`;

export const FieldLabel = styled.label`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 0.28rem;
  color: ${INK.muted};
  font-size: 0.64rem;
  font-weight: 750;
`;

export const Button = styled.button<{ $variant?: 'primary' | 'ghost' | 'danger' | 'quiet' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  min-height: 2.1rem;
  padding: 0.45rem 0.8rem;
  border: 1px solid ${p => p.$variant === 'primary' ? 'rgba(56, 189, 248, 0.6)'
    : p.$variant === 'danger' ? 'rgba(248, 113, 113, 0.45)'
      : p.$variant === 'quiet' ? 'transparent' : INK.lineStrong};
  border-radius: 9px;
  background: ${p => p.$variant === 'primary' ? 'rgba(14, 116, 144, 0.55)'
    : p.$variant === 'danger' ? 'rgba(127, 29, 29, 0.25)'
      : p.$variant === 'quiet' ? 'transparent' : 'rgba(15, 23, 42, 0.7)'};
  color: ${p => p.$variant === 'danger' ? '#fecaca' : p.$variant === 'quiet' ? INK.accentSoft : INK.strong};
  cursor: pointer;
  font: inherit;
  font-size: 0.74rem;
  font-weight: 750;
  white-space: nowrap;

  &:hover:not(:disabled) { border-color: ${p => p.$variant === 'danger' ? '#f87171' : INK.accent}; }
  &:focus-visible { outline: 2px solid ${INK.accent}; outline-offset: 2px; }
  &:disabled { cursor: not-allowed; opacity: 0.5; }
`;

export const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

export const reducedMotion = css`
  @media (prefers-reduced-motion: reduce) {
    transition: none !important;
    animation: none !important;
  }
`;
